import { useCallback, useEffect, useState } from 'react';
import editorAutocorrect from 'packages/pages/editor/EditorAutocorrectRules';
import { useInputBlur } from 'packages/hooks/useInputBlur';

const useBaseFontSettingInput = ({
    prop,
    isMixed,
    mixedValue,
    autoCorrectKey,
    disabled,
    locked,
    onSubmit,
    mapDisplayValue = (x) => x.toString(),
    mapSubmitValue = (x) => x,
}: {
    prop: any;
    isMixed: boolean;
    mixedValue: string;
    autoCorrectKey: string;
    disabled: boolean;
    locked: boolean;
    onSubmit: (value: any) => void;
    mapDisplayValue?: (value: any) => string;
    mapSubmitValue?: (value: any) => any;
}) => {
    const { onFocus, unsubscribeListener } = useInputBlur();
    const [value, setValue] = useState('');
    const [placeholder, setPlaceholder] = useState('');

    const setInitialState = useCallback(() => {
        const v: string = !isMixed ? mapDisplayValue(prop) : mixedValue;

        if (!isMixed) {
            setValue(v);
            setPlaceholder('');
        } else {
            setValue('');
            setPlaceholder(v);
        }
    }, [isMixed, mapDisplayValue, prop, mixedValue]);

    useEffect(() => {
        setInitialState();
    }, [prop, isMixed, mixedValue]);

    const applyValue = useCallback(
        (val: string) => {
            if (isMixed && val === '') {
                return;
            }

            const valueToApply = val || mapDisplayValue(prop);
            const correctValue = editorAutocorrect(autoCorrectKey, valueToApply);

            setValue(correctValue);
            onSubmit(mapSubmitValue(correctValue));
        },
        [isMixed, prop, autoCorrectKey, mapDisplayValue, mapSubmitValue, onSubmit],
    );

    const onBlur = useCallback(() => {
        applyValue(value);
        unsubscribeListener();
    }, [value, applyValue, unsubscribeListener]);

    const getValueAsNum = useCallback(() => {
        if (value) {
            return Number(value);
        }

        if (isMixed) {
            return 0;
        }

        return Number(mapDisplayValue(prop));
    }, [value, isMixed, prop, mapDisplayValue]);

    const onKeyDown = useCallback(
        (event) => {
            if (event.key === 'Enter') {
                onBlur();
                event.target.blur();
            }

            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const numberToAdd = event.key === 'ArrowDown' ? -1 : 1;
                const factor = event.shiftKey ? 10 : 1;
                applyValue(String(getValueAsNum() + numberToAdd * factor));
            }
        },
        [onBlur, getValueAsNum, applyValue],
    );

    const onChange = setValue;
    const type = Number.isNaN(Number(value)) ? 'text' : 'number';
    const isDisabled = disabled || locked;

    return { value, placeholder, isDisabled, type, onBlur, onFocus, onKeyDown, onChange } as const;
};

export { useBaseFontSettingInput };
